import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { Card, Btn, FieldLabel, inputCls, textareaCls } from "@/components/admin/ui";
import PhotoUploader from "@/components/admin/PhotoUploader";
import ColorStockEditor from "@/components/admin/ColorStockEditor";

const EMPTY = { name: "", description: "", price: "", photos: [], cover_photo: "", colors: [], category: "", shipping_price: "", status: "draft", featured: false };

export default function ProductEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(id ? null : EMPTY);
  const [slug, setSlug] = useState("");
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setCategories(r.data.categories || []));
    if (id) {
      api.get(`/admin/products/${id}`).then((r) => { setP({ ...EMPTY, ...r.data }); setSlug(r.data.slug); })
        .catch(() => { toast.error("Product not found"); nav("/admin/products"); });
    }
  }, [id, nav]);

  if (!p) return null;
  const set = (patch) => setP((prev) => ({ ...prev, ...patch }));
  const freeShipping = Number(p.shipping_price || 0) === 0;

  const save = async (preview = false) => {
    if (!p.name.trim()) return toast.error("Give your product a name.");
    setSaving(true);
    const previewTab = preview ? window.open("", "_blank") : null;
    try {
      const body = { ...p, price: Number(p.price || 0), shipping_price: Number(p.shipping_price || 0) };
      ["id", "slug", "created_at", "updated_at", "total_stock"].forEach((k) => delete body[k]);
      const { data } = id ? await api.put(`/admin/products/${id}`, body) : await api.post("/admin/products", body);
      toast.success(data.status === "published" ? "Saved — product is live" : "Saved as draft");
      setSlug(data.slug);
      if (previewTab) previewTab.location.href = `/product/${data.slug}`;
      if (!id) nav(`/admin/products/${data.id}`, { replace: true });
    } catch (e) {
      if (previewTab) previewTab.close();
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  const remove = async () => {
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    nav("/admin/products");
  };

  return (
    <div data-testid="product-editor-page">
      <Link to="/admin/products" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-4"><ArrowLeft size={15} /> Products</Link>
      <h1 className="text-2xl font-bold mb-6">{id ? p.name || "Edit product" : "Add product"}</h1>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="space-y-4">
          <Card>
            <label className="block"><FieldLabel>Product name</FieldLabel>
              <input className={inputCls} value={p.name} onChange={(e) => set({ name: e.target.value })} placeholder="What is it called?" data-testid="product-name-input" />
            </label>
            <label className="block mt-4"><FieldLabel>Description</FieldLabel>
              <textarea rows={6} className={textareaCls} value={p.description} onChange={(e) => set({ description: e.target.value })} placeholder="Tell customers about the piece — size, material, finish…" data-testid="product-description-input" />
            </label>
          </Card>
          <Card title="Photos" description="Up to 12 photos. The cover photo is shown first in the shop.">
            <PhotoUploader photos={p.photos} cover={p.cover_photo} onChange={(photos, cover_photo) => set({ photos, cover_photo })} />
          </Card>
          <Card title="Colors & stock" description="Add each color you sell and how many you have of each.">
            <ColorStockEditor colors={p.colors} onChange={(colors) => set({ colors })} />
          </Card>
          <Card title="Price & shipping">
            <div className="grid sm:grid-cols-2 gap-4">
              <label><FieldLabel>Price (USD)</FieldLabel>
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
                  <input type="number" min="0" step="0.01" className={`${inputCls} pl-7`} value={p.price} onChange={(e) => set({ price: e.target.value })} placeholder="0.00" data-testid="product-price-input" />
                </div>
              </label>
              <label><FieldLabel hint="(legacy product value)">Shipping price</FieldLabel>
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
                  <input type="number" min="0" step="0.01" className={`${inputCls} pl-7`} value={p.shipping_price} onChange={(e) => set({ shipping_price: e.target.value })} placeholder="0.00" data-testid="product-shipping-input" />
                </div>
              </label>
            </div>
            <p className="text-xs text-zinc-500 mt-3" data-testid="product-shipping-hint">Checkout shipping is a flat $4.99 per order. Eligible free-shipping discounts still apply.</p>
          </Card>
        </div>
        <div className="space-y-4">
          <Card title="Status">
            <div className="grid grid-cols-2 gap-2" data-testid="product-status-options">
              {[["draft", "Draft", "Hidden from store"], ["published", "Published", "Visible to customers"]].map(([v, l, d]) => (
                <button key={v} type="button" onClick={() => set({ status: v })} data-testid={`product-status-${v}`}
                  className={`text-left rounded-lg border p-3 transition-colors ${p.status === v ? "border-zinc-950 bg-zinc-50 ring-1 ring-zinc-950" : "border-zinc-200 hover:border-zinc-300"}`}>
                  <span className="block text-sm font-medium">{l}</span>
                  <span className="block text-xs text-zinc-500 mt-0.5">{d}</span>
                </button>
              ))}
            </div>
          </Card>
          <Card title="Organize">
            <label className="block"><FieldLabel>Category</FieldLabel>
              <input list="category-list" className={inputCls} value={p.category} onChange={(e) => set({ category: e.target.value })} placeholder="Type or choose a category" data-testid="product-category-input" />
              <datalist id="category-list">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            </label>
            <div className="flex items-center justify-between mt-5">
              <div><p className="text-sm font-medium">Feature in THE DROP</p><p className="text-xs text-zinc-500">Show on the homepage</p></div>
              <Switch checked={p.featured} onCheckedChange={(v) => set({ featured: v })} data-testid="product-featured-switch" />
            </div>
          </Card>
          {id && (
            <Btn variant="danger" className="w-full" onClick={() => setConfirmDelete(true)} data-testid="product-delete-button"><Trash2 size={15} /> Delete product</Btn>
          )}
        </div>
      </div>
      <div className="fixed bottom-0 right-0 left-0 lg:left-60 z-30 bg-white/90 backdrop-blur border-t border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-end gap-2">
          {slug && <a href={`/product/${slug}`} target="_blank" rel="noreferrer" className="hidden sm:inline text-sm text-zinc-500 hover:text-zinc-950 mr-auto" data-testid="product-view-link">View in store ↗</a>}
          <Btn variant="secondary" onClick={() => save(true)} disabled={saving} data-testid="product-save-preview-button"><Eye size={15} /> Save & preview</Btn>
          <Btn onClick={() => save(false)} disabled={saving} data-testid="product-save-button">{saving && <Loader2 size={15} className="animate-spin" />} Save</Btn>
        </div>
      </div>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="admin min-h-0 bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone. Past orders will keep their details.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="product-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
